import {
  CompoundQuestion,
  CompoundQuestionDeploymentStatus,
  CompoundQuestionTemplateResponse,
  CompoundQuestionUpdateRequest,
  GetCompoundQuestionsRequest,
  GetCompoundQuestionsResponse,
} from "types";

import API from "api/client";
import axios from "axios";
import downloadFile from "utils/download/downloadFile";
import { envConfig } from "config";
import { isSuccessStatus } from "utils/compound-questions/compoundQuestionUtils";

const { compoundQuestionsAPI } = envConfig;

const getCompoundQuestions = (params: GetCompoundQuestionsRequest): Promise<GetCompoundQuestionsResponse> => {
  const { page = 1, size = 50, query } = params;
  return API.get(compoundQuestionsAPI, { params: { page, size, query } }).then((res) => res.data);
};

const getCompoundQuestion = (id: number): Promise<CompoundQuestion> => {
  return API.get(`${compoundQuestionsAPI}${id}`).then((res) => res.data);
};

const updateCompoundQuestion = (id: number, req: CompoundQuestionUpdateRequest): Promise<CompoundQuestion> => {
  return API.put(`${compoundQuestionsAPI}${id}`, req).then((res) => res.data);
};

const deleteCompoundQuestion = (id: number): Promise<void> => {
  return API.delete(`${compoundQuestionsAPI}${id}`).then((res) => res.data);
};

const getCompoundQuestionDeploymentStatus = (id: number): Promise<CompoundQuestionDeploymentStatus> => {
  return API.get(`${compoundQuestionsAPI}${id}/deployment-status`).then((res) => res.data);
};

const downloadCompoundQuestionTemplate = async (): Promise<void> => {
  const response = await API.get(`${compoundQuestionsAPI}template/download`, { responseType: "blob" });
  downloadFile(response);
};

/**
 * Uploads a filled-in template file. Used by both validate and create.
 *
 * Unlike most API calls, this also resolves (instead of throwing) for a 422, which the
 * API returns only for template validation errors, so the page can list them inline.
 * The body is left un-camelized so it is shown exactly as the API returned it.
 * Every other failure goes through the client's normal error handling.
 */
const uploadCompoundQuestionTemplate = (url: string, file: File): Promise<CompoundQuestionTemplateResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  return API.post(url, formData, {
    validateStatus: (status) => isSuccessStatus(status) || status === 422,
    transformResponse: axios.defaults.transformResponse,
  }).then((res) => ({ status: res.status, body: res.data }));
};

const validateCompoundQuestionTemplate = (file: File): Promise<CompoundQuestionTemplateResponse> => {
  return uploadCompoundQuestionTemplate(`${compoundQuestionsAPI}template/validate`, file);
};

const createCompoundQuestionFromTemplate = (file: File): Promise<CompoundQuestionTemplateResponse> => {
  return uploadCompoundQuestionTemplate(`${compoundQuestionsAPI}template`, file);
};

export default {
  list: getCompoundQuestions,
  get: getCompoundQuestion,
  update: updateCompoundQuestion,
  delete: deleteCompoundQuestion,
  getDeploymentStatus: getCompoundQuestionDeploymentStatus,
  downloadTemplate: downloadCompoundQuestionTemplate,
  validateTemplate: validateCompoundQuestionTemplate,
  createFromTemplate: createCompoundQuestionFromTemplate,
};
